import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Stack,
  Avatar,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  PersonOutline as PersonOutlineIcon,
  Refresh as RefreshIcon,
  LocalPharmacy as LocalPharmacyIcon,
  HourglassTop as HourglassTopIcon,
  HighlightOff as HighlightOffIcon,
} from '@mui/icons-material';
import { loadPharmacyPatientsChart, loadPharmacyPatientsMedcount, loadPharmacyUserMed } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';

// ── Theme tokens (mirrors Procurement Home.js) ─────────────────────────────
const PRIMARY      = '#41a386';
const PRIMARY_DARK = '#2f8068';
const PRIMARY_BG   = 'rgba(65,163,134,0.10)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

// Status palette — single source of truth
const STATUS_PALETTE = {
  completed:  { tint: 'rgba(16,185,129,0.10)', strong: '#15803D', label: 'Completed' },
  pending:    { tint: 'rgba(217,119,6,0.10)',  strong: '#92400E', label: 'Pending'   },
  processing: { tint: 'rgba(14,165,233,0.10)', strong: '#0369A1', label: 'Processing'},
  cancelled:  { tint: 'rgba(220,38,38,0.10)',  strong: '#991B1B', label: 'Cancelled' },
};

const StatusPill = ({ status }) => {
  const key = String(status || '').toLowerCase();
  const s = STATUS_PALETTE[key] || STATUS_PALETTE.pending;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center',
      px: 1, py: 0.25, borderRadius: 1,
      bgcolor: s.tint, color: s.strong,
      fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>
      {s.label}
    </Box>
  );
};

const Home = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('Today');
  const [selectedActivityPeriod, setSelectedActivityPeriod] = useState('Today');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [currentUser, setcurrentUser] = useState('');

  const [prescriptionData, setPrescriptionData] = useState([]);
  const [statCards, setStatCards] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  // Load user info on mount
  useEffect(() => {
    const savedUserInfo = localStorage.getItem('userInfo');
    if (savedUserInfo) {
      const userInfo = JSON.parse(savedUserInfo);
      setcurrentUser(userInfo.name || '');
    }
  }, []);

  // ── Helpers ─────────────────────────────────────────────────────────────
  const isValidDate = (date) => date instanceof Date && !isNaN(date.getTime());
  const createSafeDate = (dateValue) => {
    if (!dateValue) return null;
    const d = new Date(dateValue);
    return isValidDate(d) ? d : null;
  };

  // ── Loaders ─────────────────────────────────────────────────────────────
  const loadUserMed = async (user = currentUser) => {
    try {
      if (!user) return;
      setLoading(true);
      const response = await axios.post(loadPharmacyUserMed, { currentUser: user });
      if (response.data.success) setRecentActivity(response.data.results);
    } catch (error) {
      console.error('Error loading user med data:', error);
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load recent activity data',
        icon: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysischart = async () => {
    try {
      const periodMap = {
        'Today': 'today',
        'This Week': 'week',
        'This Month': 'month',
        'This Year': 'year',
        'All': 'all',
      };
      const period = periodMap[selectedPeriod] || 'month';
      const response = await axios.get(`${loadPharmacyPatientsChart}?period=${period}&limit=25`);
      if (response.data.success) setPrescriptionData(response.data.results);
    } catch (error) {
      console.error('Error loading analysis chart:', error);
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load chart data',
        icon: 'error',
      });
    }
  };

  // Map API icon strings to MUI components
  const iconMap = {
    'ScheduleIcon': ScheduleIcon,
    'CheckCircle': CheckCircleIcon,
    'WarningIcon': WarningIcon,
    'AssignmentIcon': AssignmentIcon,
    // Allow API to also send these explicit names if needed
    'HourglassTopIcon': HourglassTopIcon,
    'HighlightOffIcon': HighlightOffIcon,
  };

  const loadAnalysisInfor = async () => {
    try {
      const response = await axios.get(loadPharmacyPatientsMedcount);
      if (response.data.success) setStatCards(response.data.results);
    } catch (error) {
      console.error('Error loading analysis info:', error);
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load statistics',
        icon: 'error',
      });
    }
  };

  useEffect(() => { loadAnalysischart(); /* eslint-disable-next-line */ }, [selectedPeriod]);

  useEffect(() => {
    if (currentUser) loadUserMed(currentUser);
    /* eslint-disable-next-line */
  }, [currentUser]);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([loadAnalysisInfor()]);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
    /* eslint-disable-next-line */
  }, []);

  // ── Derived data ────────────────────────────────────────────────────────
  const getFilteredData = useMemo(() => {
    if (prescriptionData.length === 0) return [];
    const grouped = prescriptionData.reduce((acc, item) => {
      const existing = acc.find(med => med.name === item.name);
      if (existing) existing.value += item.quantity;
      else acc.push({ name: item.name, value: item.quantity, color: item.color });
      return acc;
    }, []);
    return grouped.sort((a, b) => b.value - a.value).slice(0, 10);
  }, [prescriptionData]);

  const getFilteredActivityData = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    let filteredData = [];
    switch (selectedActivityPeriod) {
      case 'Today':
        filteredData = recentActivity.filter(item => {
          const itemDate = createSafeDate(item.dispensedAt);
          if (!itemDate) return false;
          const itemDateOnly = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());
          return itemDateOnly.getTime() === today.getTime();
        });
        break;
      case 'This Week': {
        const weekStart = new Date(today); weekStart.setDate(today.getDate() - 6); weekStart.setHours(0,0,0,0);
        const weekEnd = new Date(today); weekEnd.setHours(23,59,59,999);
        filteredData = recentActivity.filter(item => {
          const itemDate = createSafeDate(item.dispensedAt);
          return itemDate && itemDate >= weekStart && itemDate <= weekEnd;
        });
        break;
      }
      case 'This Month': {
        const monthStart = new Date(today); monthStart.setDate(today.getDate() - 29); monthStart.setHours(0,0,0,0);
        const monthEnd = new Date(today); monthEnd.setHours(23,59,59,999);
        filteredData = recentActivity.filter(item => {
          const itemDate = createSafeDate(item.dispensedAt);
          return itemDate && itemDate >= monthStart && itemDate <= monthEnd;
        });
        break;
      }
      case 'This Year': {
        const yearStart = new Date(now.getFullYear(), 0, 1); yearStart.setHours(0,0,0,0);
        const yearEnd = new Date(today); yearEnd.setHours(23,59,59,999);
        filteredData = recentActivity.filter(item => {
          const itemDate = createSafeDate(item.dispensedAt);
          return itemDate && itemDate >= yearStart && itemDate <= yearEnd;
        });
        break;
      }
      case 'All':
        filteredData = recentActivity.filter(item => createSafeDate(item.dispensedAt));
        break;
      default:
        filteredData = recentActivity;
    }

    return filteredData.sort((a, b) => {
      const dateA = createSafeDate(a.dispensedAt);
      const dateB = createSafeDate(b.dispensedAt);
      if (!dateA || !dateB) return 0;
      return dateB - dateA;
    });
  }, [selectedActivityPeriod, recentActivity]);

  const filterPeriods = ['Today', 'This Week', 'This Month', 'This Year', 'All'];
  const topMedications = getFilteredData;
  const maxValue = topMedications.length > 0 ? Math.max(...topMedications.map(m => m.value)) : 1;

  // ── Handlers ────────────────────────────────────────────────────────────
  const handlePeriodChange         = (_, p) => p && setSelectedPeriod(p);
  const handleActivityPeriodChange = (_, p) => { if (!p) return; setSelectedActivityPeriod(p); setPage(0); };
  const handleChangePage           = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage    = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  const formatTime = (dateString) => new Date(dateString).toLocaleTimeString('en-US',
    { hour: '2-digit', minute: '2-digit', hour12: true });
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US',
    { month: 'short', day: 'numeric', year: 'numeric' });

  const getDateRangeText = (period = selectedPeriod) => {
    const now = new Date();
    switch (period) {
      case 'Today':      return now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      case 'This Week': {
        const weekStart = new Date(now); weekStart.setDate(now.getDate() - 6);
        return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      }
      case 'This Month': {
        const monthStart = new Date(now); monthStart.setDate(now.getDate() - 29);
        return `${monthStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      }
      case 'This Year':  return `Jan 1 – ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      case 'All':        return 'All time data';
      default:           return '';
    }
  };

  // ── UI ──────────────────────────────────────────────────────────────────
  // Compact period selector (re-used twice)
  const PeriodToggle = ({ value, onChange }) => (
    <ToggleButtonGroup
      value={value} exclusive size="small"
      onChange={onChange}
      sx={{
        '& .MuiToggleButton-root': {
          textTransform: 'none', py: 0.4, px: 1.25,
          fontSize: '0.74rem', fontWeight: 600,
          borderRadius: '6px !important', mx: 0.2,
          color: MUTED, borderColor: BORDER,
        },
        '& .Mui-selected': {
          bgcolor: `${PRIMARY} !important`,
          color: 'white !important',
          borderColor: `${PRIMARY} !important`,
        },
      }}
    >
      {filterPeriods.map(p => (
        <ToggleButton key={p} value={p}>{p}</ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  // Soft greeting based on time-of-day
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f8fafc', minHeight: '100vh', py: 2.5 }}>
      <Container maxWidth="xl">

        {/* Loading backdrop */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1, display: 'flex', flexDirection: 'column', gap: 2 }}
          open={loading}
        >
          <CircularProgress sx={{ color: PRIMARY }} size={48} />
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Loading dashboard…</Typography>
        </Backdrop>

        {/* Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography sx={{ ...LABEL_SX, color: PRIMARY, mb: 0.25 }}>
              Pharmacy · Dashboard
            </Typography>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
              {greeting}{currentUser ? `, ${currentUser}` : ''}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25, maxWidth: 760 }}>
              At-a-glance view of dispensing volume, top-prescribed medications, and your most
              recent dispensing activity. Use the period toggles on each panel to switch between
              today, this week, this month, this year, or all-time.
            </Typography>
          </Box>
          <Tooltip title="Refresh data" arrow>
            <IconButton
              size="small"
              onClick={async () => {
                setLoading(true);
                try { await Promise.all([loadAnalysisInfor(), loadAnalysischart(), loadUserMed(currentUser)]); }
                finally { setLoading(false); }
              }}
              sx={{
                border: `1px solid ${BORDER}`, bgcolor: '#fff', color: MUTED,
                '&:hover': { bgcolor: '#f1f5f9', color: STRONG },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Stats strip */}
        <Grid container spacing={1.25} sx={{ mb: 2 }}>
          {statCards.length === 0 && [0,1,2,3].map(i => (
            <Grid item xs={6} md={3} key={i}>
              <Paper elevation={0} sx={{
                p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff',
                display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 64,
              }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.5,
                  bgcolor: '#f1f5f9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <LocalPharmacyIcon sx={{ fontSize: 18, color: '#cbd5e1' }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={LABEL_SX}>Loading</Typography>
                  <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#cbd5e1', lineHeight: 1.1, mt: 0.25 }}>
                    —
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}

          {statCards.map((card, idx) => {
            const IconComponent = iconMap[card.icon] || ScheduleIcon;
            return (
              <Grid item xs={6} md={3} key={idx}>
                <Paper elevation={0} sx={{
                  p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 1.5,
                    bgcolor: card.iconBg || PRIMARY_BG,
                    color:   card.iconColor || PRIMARY,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <IconComponent sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={LABEL_SX}>{card.title}</Typography>
                    <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: STRONG, lineHeight: 1.1, mt: 0.25 }}>
                      {card.value}
                    </Typography>
                    {card.subtitle && (
                      <Typography sx={{ fontSize: '0.7rem', color: MUTED, mt: 0.15 }} noWrap>
                        {card.subtitle}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Top Medications */}
        <Paper elevation={0} sx={{
          mb: 2, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff', overflow: 'hidden',
        }}>
          <Box sx={{ p: 1.75, borderBottom: `1px solid ${BORDER}` }}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={1.25}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box sx={{
                  width: 32, height: 32, borderRadius: 1.25,
                  bgcolor: PRIMARY_BG, color: PRIMARY,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <TrendingUpIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography sx={LABEL_SX}>Insights</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
                    Top 10 prescribed medicines
                  </Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                    {getDateRangeText(selectedPeriod)} · {topMedications.length} med{topMedications.length === 1 ? '' : 's'} tracked
                  </Typography>
                </Box>
              </Stack>
              <PeriodToggle value={selectedPeriod} onChange={handlePeriodChange} />
            </Stack>
          </Box>

          <Box sx={{ p: 2 }}>
            {topMedications.length > 0 ? (
              <Stack spacing={0.85}>
                {topMedications.map((med, idx) => {
                  const pct = (med.value / maxValue) * 100;
                  return (
                    <Stack
                      key={`${med.name}-${idx}`}
                      direction="row"
                      alignItems="center"
                      spacing={1.25}
                      sx={{ minHeight: 30 }}
                    >
                      {/* Rank */}
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%',
                        bgcolor: idx < 3 ? PRIMARY_BG : '#f1f5f9',
                        color:   idx < 3 ? PRIMARY    : MUTED,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.7rem', fontWeight: 800, flexShrink: 0,
                      }}>
                        {idx + 1}
                      </Box>
                      {/* Name */}
                      <Typography sx={{
                        width: { xs: 140, sm: 200, md: 240 },
                        fontSize: '0.82rem', fontWeight: 600, color: STRONG,
                        flexShrink: 0,
                      }} noWrap title={med.name}>
                        {med.name}
                      </Typography>
                      {/* Bar */}
                      <Box sx={{
                        flex: 1, position: 'relative',
                        height: 14, bgcolor: '#f1f5f9', borderRadius: 999, overflow: 'hidden',
                      }}>
                        <Box sx={{
                          position: 'absolute', inset: 0, width: `${Math.max(pct, 2)}%`,
                          bgcolor: med.color || PRIMARY,
                          borderRadius: 999,
                          transition: 'width 0.4s ease',
                        }} />
                      </Box>
                      {/* Value */}
                      <Typography sx={{
                        width: 56, textAlign: 'right',
                        fontSize: '0.84rem', fontWeight: 800, color: STRONG,
                        fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                      }}>
                        {med.value.toLocaleString()}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                py: 6, color: '#94a3b8',
              }}>
                <TrendingUpIcon sx={{ fontSize: 36, mb: 1, opacity: 0.6 }} />
                <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG }}>
                  No prescription data
                </Typography>
                <Typography sx={{ fontSize: '0.74rem', color: MUTED, mt: 0.25 }}>
                  Nothing was dispensed for {selectedPeriod.toLowerCase()}.
                </Typography>
              </Box>
            )}

            {/* Footer summary */}
            {topMedications.length > 0 && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box>
                  <Typography sx={LABEL_SX}>Tracked</Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: STRONG, lineHeight: 1.1 }}>
                    {topMedications.length}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={LABEL_SX}>Total dispensed</Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: PRIMARY, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                    {topMedications.reduce((s, m) => s + m.value, 0).toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={LABEL_SX}>Period</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: STRONG, lineHeight: 1.1 }}>
                    {selectedPeriod}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Recent Dispensing Activity */}
        <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff', overflow: 'hidden' }}>
          <Box sx={{ p: 1.75, borderBottom: `1px solid ${BORDER}` }}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={1.25}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box sx={{
                  width: 32, height: 32, borderRadius: 1.25,
                  bgcolor: PRIMARY_BG, color: PRIMARY,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <AssignmentIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography sx={LABEL_SX}>Activity</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
                    Recent dispensing activity
                  </Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
                    {getDateRangeText(selectedActivityPeriod)} · {getFilteredActivityData.length} record{getFilteredActivityData.length === 1 ? '' : 's'}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <PeriodToggle value={selectedActivityPeriod} onChange={handleActivityPeriodChange} />
                <Tooltip title="Reload activity" arrow>
                  <IconButton
                    size="small"
                    onClick={() => currentUser && loadUserMed(currentUser)}
                    sx={{
                      border: `1px solid ${BORDER}`, bgcolor: '#fff', color: MUTED,
                      '&:hover': { bgcolor: '#f1f5f9', color: STRONG },
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>

          {getFilteredActivityData.length > 0 ? (
            <>
              <TableContainer sx={{ maxHeight: '60vh' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, ...LABEL_SX, py: 1.25 } }}>
                      <TableCell>Patient</TableCell>
                      <TableCell>Medication</TableCell>
                      <TableCell align="center" sx={{ width: 80 }}>Qty</TableCell>
                      <TableCell>Prescribed by</TableCell>
                      <TableCell sx={{ width: 110 }}>Date</TableCell>
                      <TableCell sx={{ width: 90 }}>Time</TableCell>
                      <TableCell align="center" sx={{ width: 110 }}>Status</TableCell>
                      <TableCell>Pharmacist</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredActivityData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((activity) => (
                        <TableRow key={activity.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Avatar sx={{ width: 26, height: 26, bgcolor: PRIMARY_BG, color: PRIMARY }}>
                                <PersonOutlineIcon sx={{ fontSize: 16 }} />
                              </Avatar>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: STRONG }}>
                                {activity.patientId}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, color: STRONG }}>
                            {activity.medication}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              minWidth: 36, height: 22, px: 0.85,
                              borderRadius: 1, bgcolor: '#f1f5f9', color: STRONG,
                              fontSize: '0.78rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                            }}>
                              {activity.quantity}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', color: STRONG }}>
                            {activity.prescribedBy}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.78rem', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                            {formatDate(activity.dispensedAt)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.78rem', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
                            {formatTime(activity.dispensedAt)}
                          </TableCell>
                          <TableCell align="center">
                            <StatusPill status={activity.status} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', color: STRONG }}>
                            {activity.pharmacist}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={getFilteredActivityData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: `1px solid ${BORDER}`,
                  '.MuiTablePagination-toolbar': { minHeight: 44 },
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    fontSize: '0.78rem', color: MUTED,
                  },
                  '.MuiTablePagination-select': { fontSize: '0.78rem', fontWeight: 700, color: STRONG },
                }}
              />
            </>
          ) : (
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              p: 5, gap: 1,
            }}>
              <AssignmentIcon sx={{ fontSize: 36, color: '#cbd5e1' }} />
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: STRONG }}>
                No dispensing activity
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: MUTED }}>
                Nothing recorded for {selectedActivityPeriod.toLowerCase()}.
              </Typography>
              <Button
                size="small" variant="outlined"
                startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                onClick={() => currentUser && loadUserMed(currentUser)}
                sx={{
                  textTransform: 'none', fontWeight: 600, mt: 1,
                  borderColor: BORDER, color: MUTED,
                  '&:hover': { borderColor: PRIMARY, color: PRIMARY, bgcolor: PRIMARY_BG },
                }}
              >
                Try refreshing
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Home;
